import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '@app/store';
import { getStationsState } from '@app/store/data/globals';
import { gnssmApi } from '@app/store/gnssm/codegen/gnssmApi';

import { CustomStation, GroundStation, StationType } from '@app/models';

import { fixed } from '@app/utils/Math';

/* *********************** *
 *    Type definitions     *
 * *********************** */
export interface StationsState {
  stations: GroundStation[];
  custom: CustomStation[];
  editing: CustomStation | null;
}

/* ************************* *
 *    Utitilty functions     *
 * ************************* */
export const createCustomStation = (latlng?: L.LatLng): CustomStation => ({
  code: 'new',
  type: StationType.Custom,
  label: 'Default Marker',
  latitude: Number.parseFloat(fixed(latlng?.lat, 6)) || 0,
  longitude: Number.parseFloat(fixed(latlng?.lng, 6)) || 0,
  height: 0,
});

export const isSaved = (station: CustomStation) => station.code !== 'new';

export const isRealStation = (station: GroundStation | CustomStation) =>
  station.type === StationType.Real;
export const isCustomStation = (station: GroundStation | CustomStation) =>
  station.type === StationType.Custom;

/* ********************* *
 *    Slice creation     *
 * ********************* */
const initialState: StationsState = {
  stations: [],
  custom: [],
  editing: null,
};

const stationsSlice = createSlice({
  name: 'stations',
  initialState,
  reducers: {
    addCustomStation: (state: StationsState, action: PayloadAction<CustomStation>) => {
      state.custom.push(action.payload);
    },
    removeCustomStation: (state: StationsState, action: PayloadAction<CustomStation>) => {
      state.custom = state.custom.filter((station) => station.code !== action.payload.code);
      return state;
    },
    updateCustomStation: (state: StationsState, action: PayloadAction<CustomStation>) => {
      const index = state.custom.findIndex((station) => station.code === action.payload.code);
      state.custom[index] = action.payload;
    },
    editCustomStation: (state: StationsState, action: PayloadAction<CustomStation | null>) => {
      state.editing = action.payload;
    },
  },
  extraReducers: (builder) => {},
});

/* *********************** *
 *    Export selectors     *
 * *********************** */

export const getCustomStations = (state: RootState) => getStationsState(state).custom;

export const getEditStation = (state: RootState) => getStationsState(state).editing;

// NEW REST API selectors (do not use anything in the state!)
export const getStationsWithPositions = () =>
  createSelector(
    [
      // TODO(bryd_re): currently not used!
      (state: RootState) => gnssmApi.endpoints.getAllStations.select({})(state),
      (state: RootState, start: string, end: string, onlyLatest: boolean) =>
        gnssmApi.endpoints.getStationPositions.select({ start, end })(state),
    ],
    // TODO(bryd_re): currently not used!
    (stationsResults, positionsResults) => {
      const stationsData = stationsResults.data ?? [];
      const positionsData = positionsResults.data ?? [];
      const stations: GroundStation[] = [];
      stationsData.map((station) => {
        const position = positionsData.find((s) => s.code == station.code);
        if (position) {
          stations.push({
            ...station,
            ...position,
            type: StationType.Real,
            label: station.code, // TODO(bryd_re): custom station needs label, rather separate the two?
          });
        }
      });
      const isQuerying =
        stationsResults.isLoading ||
        stationsResults.isUninitialized ||
        positionsResults.isLoading ||
        positionsResults.isUninitialized;

      return { stations, isQuerying };
    },
  );

/* ********************* *
 *    Export actions     *
 * ********************* */
export const { addCustomStation, updateCustomStation, removeCustomStation, editCustomStation } =
  stationsSlice.actions;

/* ********************* *
 *    Export reducer     *
 * ********************* */
export default stationsSlice.reducer;
